import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit} from "@angular/core";
import {Compbaser} from "../../../components/compbaser";
import {LocalStoreService, StoreSelectorsService} from "../../../services";
import {filter} from "rxjs/operators";

interface ISectionPref {
    minimized?: boolean;
}

type ISectionPrefMap = {[sectionId: string]: ISectionPref};

/** Minimize/expand chrome that wraps any home section. Each section
 *  declares its id, title, and icon; the wrapper handles:
 *   - a slim rail above the section with one toggle button
 *   - collapsed state (thin clickable title strip)
 *   - persistence into ILocalStorageConfig.homeSectionPrefs (JSON map)
 *
 *  The persisted blob looks like:
 *      { "recents": { "minimized": true }, "showcase": { "minimized": false } }
 *  Keys are kept compact so adding more sections later doesn't grow
 *  the localStorage value unbounded. */
@Component({
    selector: "app-home-section",
    templateUrl: "./home-section.component.html",
    styleUrls: ["./home-section.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class HomeSectionComponent extends Compbaser implements OnInit {
    /** Stable id used as the localStorage map key. Don't rename without
     *  a migration — old users would lose their preference. */
    @Input({required: true}) sectionId!: string;
    /** Display title shown on the minimized strip and as the aria-label. */
    @Input() title = "";
    /** Material icon name shown next to the title in the minimized strip. */
    @Input() icon = "widgets";

    minimized = false;

    constructor(
        private localStoreService: LocalStoreService,
        private storeSelector: StoreSelectorsService,
        private cdr: ChangeDetectorRef,
    ) {
        super();
    }

    ngOnInit(): void {
        this.applyPrefs(this.readPrefs());
        // Other sections in the page can mutate the prefs blob on the
        // same tick — listen for store updates and reapply so chrome
        // stays in sync without us holding a reference to siblings.
        this.cod(
            this.storeSelector.getLocalStoreUpdates("ILocalStorageConfig")
                .pipe(filter((v) => v !== null))
                .subscribe(() => {
                    this.applyPrefs(this.readPrefs());
                }),
        );
    }

    onToggleMinimize(ev: Event): void {
        ev.stopPropagation();
        this.minimized = !this.minimized;
        this.writePrefs();
        this.cdr.markForCheck();
    }

    /** Clicking the collapsed strip expands the section. Anywhere on
     *  the strip except the pin button works — pin should stay sticky
     *  even while minimized. */
    onExpand(): void {
        if (!this.minimized) return;
        this.minimized = false;
        this.writePrefs();
        this.cdr.markForCheck();
    }

    /** Load the full map, return this section's slot (defaulted). */
    private readPrefs(): ISectionPref {
        const raw = this.localStoreService.getStorageValConfig("homeSectionPrefs") || "";
        if (!raw) return {};
        let map: ISectionPrefMap = {};
        try {
            map = JSON.parse(raw) || {};
        } catch {
            // corrupted blob — start fresh rather than throwing in CD
            map = {};
        }
        return map[this.sectionId] || {};
    }

    private applyPrefs(p: ISectionPref): void {
        this.minimized = !!p.minimized;
        this.cdr.markForCheck();
    }

    /** Merge this section's minimized state back into the map. We read
     *  before writing so we never clobber a sibling section's preference
     *  on the same change-detection cycle. sync=true so the value
     *  round-trips through the server like the other config keys. */
    private writePrefs(): void {
        const raw = this.localStoreService.getStorageValConfig("homeSectionPrefs") || "";
        let map: ISectionPrefMap = {};
        if (raw) {
            try {
                map = JSON.parse(raw) || {};
            } catch {
                map = {};
            }
        }
        map[this.sectionId] = {minimized: this.minimized};
        this.localStoreService.setStorageValConfig("homeSectionPrefs", JSON.stringify(map));
    }
}
